import { useMutation, useQueryClient } from 'react-query'
import { useToast } from '../contexts/NotificationContext'
import { extractErrorMessage } from '../utils/formatUtils'

interface MutationConfig<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>
  successMessage?: string
  errorMessage?: string
  invalidateQueries?: string | string[]
  onSuccess?: (data: TData, variables: TVariables, context: any) => void | Promise<void>
  onError?: (error: any, variables: TVariables, context: any) => void | Promise<void>
  onSettled?: (data: TData | undefined, error: any, variables: TVariables, context: any) => void | Promise<void>
}

/**
 * Hook genérico para mutations com toast e invalidação de queries
 */
export function useMutationWithToast<TData = any, TVariables = any>(
  config: MutationConfig<TData, TVariables>
) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const {
    mutationFn,
    successMessage,
    errorMessage = 'Erro ao processar solicitação',
    invalidateQueries,
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
    onSettled: onSettledCallback,
  } = config

  return useMutation<TData, any, TVariables>(mutationFn, {
    onSuccess: async (data, variables, context) => {
      if (successMessage) {
        toast.success('Sucesso', successMessage)
      }

      if (invalidateQueries) {
        const queries = Array.isArray(invalidateQueries) ? invalidateQueries : [invalidateQueries]
        queries.forEach((queryKey) => {
          // Invalidar todas as queries que começam com a chave (incluindo queries com filtros)
          queryClient.invalidateQueries(queryKey, { exact: false })
        })
      }

      if (onSuccessCallback) {
        await onSuccessCallback(data, variables, context)
      }
    },
    onError: async (error, variables, context) => {
      const message = extractErrorMessage(error, errorMessage)
      toast.error('Erro', message)

      if (onErrorCallback) {
        await onErrorCallback(error, variables, context)
      }
    },
    onSettled: async (data, error, variables, context) => {
      if (onSettledCallback) {
        await onSettledCallback(data, error, variables, context)
      }
    },
  })
}
